import type { Dimension } from "building-types/dimensions/dimensionTypes";
import { isGeneratedExteriorDimension } from "building-types/dimensions/dimensionTypes";
import type { AutoDimensionRuntimeHandle } from "building-types/handles/docEntityHandleTypes";
import { exteriorDimensionCategoryReplaceMap } from "building-types/views/sheetGeometryCategory";
import { multimapFlatten } from "common/data/multimap";
import { makeDimension } from "doc/dimensions";
import { makeSheetGeometryFromDimensions } from "doc/plans/planDimensions";
import { type PlanGenerationContext, PlanGeometryBuilder } from "doc/plans/planGeometrySource";
import { getReportingUnitRoomIds } from "./reportingUnitRooms";

export function makeSheetGeometryFromExteriorDimensionsByLevel({
  building,
  levelId,
  dimensionPrecision,
  planRotationRads,
  selectedReportingUnitLabelId,
}: PlanGenerationContext) {
  // Skip exterior auto dimensions when a reporting unit is selected. Guard on the resolved room
  // set (not the raw label) to stay consistent with the room filtering used everywhere else.
  if (getReportingUnitRoomIds(building, selectedReportingUnitLabelId)) {
    return new PlanGeometryBuilder();
  }
  const levelGeneratedDimensions = building?.exteriorDimensions[levelId] ?? {};
  const exteriorDimensions = multimapFlatten(levelGeneratedDimensions).filter(isGeneratedExteriorDimension);

  const displayDims: Dimension[] = [];
  exteriorDimensions.forEach((dim) =>
    makeDimension(displayDims, {
      ...dim,
      dimensionPrecision,
      handle: dim.docEntityHandle as AutoDimensionRuntimeHandle | undefined,
    }),
  );

  const dimensionPlanGeometries = makeSheetGeometryFromDimensions({
    handle: displayDims.map((dim) => dim.handle),
    dimensions: displayDims,
    interiorDimensionType: building.settings.planset.interiorDimensionType,
    planRotationRads,
  });

  const builder = new PlanGeometryBuilder();
  builder.addAllFrom(dimensionPlanGeometries, exteriorDimensionCategoryReplaceMap);
  return builder;
}
